import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostBySlug } from '@/hooks/useBlog';

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { i18n, t } = useTranslation('landing');
  const { data: post, isLoading, error } = usePostBySlug(slug || '');

  const isZh = i18n.language === 'zh-TW';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--navy-deep)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--cyan-electric)]" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[var(--navy-deep)] flex items-center justify-center">
        <div className="text-center px-6">
          <h1 className="text-4xl font-bold text-white mb-4">
            {isZh ? '找不到文章' : 'Post Not Found'}
          </h1>
          <p className="text-gray-400 mb-8">
            {isZh
              ? '您要找的文章不存在或已被移除。'
              : "The post you're looking for doesn't exist or has been removed."}
          </p>
          <Link
            to="/?panel=blog"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--cyan-electric)] text-black font-medium rounded-lg hover:bg-[var(--cyan-electric)]/90 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t('stories.backToStories', isZh ? '返回故事列表' : 'Back to Stories')}
          </Link>
        </div>
      </div>
    );
  }

  const title = isZh && post.title_zh ? post.title_zh : post.title;
  const content = isZh && post.content_zh ? post.content_zh : post.content;

  return (
    <div className="min-h-screen bg-[var(--navy-deep)]">
      {/* Hero / Cover Image */}
      {post.cover_image && (
        <div className="h-[40vh] md:h-[50vh] relative">
          <img
            src={post.cover_image}
            alt={title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy-deep)] via-[var(--navy-deep)]/50 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className={`max-w-3xl mx-auto px-6 ${post.cover_image ? '-mt-32 relative z-10' : 'pt-24'}`}>
        {/* Back link */}
        <Link
          to="/?panel=blog"
          className="inline-flex items-center gap-2 text-[var(--cyan-electric)] hover:underline mb-8"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          {t('stories.backToStories', isZh ? '返回故事列表' : 'Back to Stories')}
        </Link>

        {/* Category badge */}
        <span className="inline-block px-3 py-1 bg-[var(--amber-warm)]/20 text-[var(--amber-warm)] text-sm font-medium rounded uppercase tracking-wider mb-4">
          {post.category}
        </span>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
          {title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-gray-400 mb-12 pb-8 border-b border-white/10">
          {post.author && (
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>{post.author}</span>
            </div>
          )}
          {post.published_at && (
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                {new Date(post.published_at).toLocaleDateString(isZh ? 'zh-TW' : 'en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}
        </div>

        {/* Content - rendered HTML from Google Docs */}
        <article
          className="blog-content prose prose-lg prose-invert max-w-none
            prose-headings:text-white prose-headings:font-bold
            prose-p:text-gray-300 prose-p:leading-relaxed
            prose-a:text-[var(--cyan-electric)] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white
            prose-ul:text-gray-300 prose-ol:text-gray-300
            prose-li:marker:text-[var(--cyan-electric)]
            prose-blockquote:border-l-[var(--cyan-electric)] prose-blockquote:text-gray-400
            prose-img:rounded-lg prose-img:mx-auto
            prose-hr:border-white/10
            prose-table:border-collapse
            prose-th:border prose-th:border-white/20 prose-th:bg-white/5 prose-th:px-4 prose-th:py-2 prose-th:text-white
            prose-td:border prose-td:border-white/10 prose-td:px-4 prose-td:py-2
            pb-12"
          dangerouslySetInnerHTML={{ __html: content || '' }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="py-8 border-t border-white/10">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-gray-400"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bottom navigation */}
        <div className="py-12 border-t border-white/10">
          <Link
            to="/?panel=blog"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/10 text-white font-medium rounded-lg hover:bg-white/10 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {t('stories.backToStories', isZh ? '返回故事列表' : 'Back to Stories')}
          </Link>
        </div>
      </div>
    </div>
  );
}
